import type { Table } from '@tanstack/react-table';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { CheckIcon } from '@/icons';
import { MonthlyReportData } from '@/services/payment/payment.types';
import { MONTHLY_REPORT_TITLE_ACTIONS_ID } from '../constants';
import { ApproveAllConfirmationDialog } from './confirmation/approveAll';

interface BulkApproveButtonProps {
  table?: Table<MonthlyReportData>;
}

/** "Bulk Approve (n)" next to the page title (see MonthlyReportTitle), shown while rows are selected. */
export function BulkApproveButton({ table }: BulkApproveButtonProps) {
  if (!table) {
    throw new Error('either pass a `table` instance or use it inside DataTable.');
  }
  const { t } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();
  const [slot, setSlot] = React.useState<HTMLElement | null>(null);
  const rows = table.getFilteredSelectedRowModel().rows;

  // The slot lives in the layout header, which mounts with this page; look it up once mounted.
  React.useEffect(() => {
    setSlot(document.getElementById(MONTHLY_REPORT_TITLE_ACTIONS_ID));
  }, []);

  if (!slot || rows.length === 0) return null;

  const onBulkApprove = () => {
    openConfirmation({
      title: t('common.page.modal.confirmation.title'),
      description: t('paymentManagement.affiliatePaymentLogs.monthlyReport.modal.approveAllDescription'),
      cancelLabel: t('common.page.modal.no'),
      content: <ApproveAllConfirmationDialog selectedData={rows.map((row) => row.original)} />,
      actionLabel: t('common.page.modal.yes'),
      onAction: () => {},
      onCancel: () => {},
      size: 'lg',
      variant: 'default',
    });
  };

  return createPortal(
    <button
      type="button"
      onClick={onBulkApprove}
      className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-foreground underline underline-offset-2"
    >
      <CheckIcon className="size-4" aria-hidden="true" />
      {t('paymentManagement.affiliatePaymentLogs.monthlyReport.button.bulkApprove')} ({rows.length})
    </button>,
    slot,
  );
}
